<template>
  <div class="budget-gauge">
    <div class="budget-gauge__header">
      <span class="budget-gauge__spending">
        Spending
        <strong :class="{ 'budget-gauge__amount--warning': willExceedBudget }">{{
          formatAmount(pendingAmountMinor)
        }}</strong>
        out of <strong>{{ formatAmount(remainingMinor) }}</strong> available
      </span>
      <span class="budget-gauge__total">Total budget: {{ formatAmount(totalMinor) }}</span>
    </div>

    <div class="budget-gauge__bar">
      <div class="budget-gauge__bar-spent" :style="{ width: `${spentPercentage}%` }"></div>
      <div
        class="budget-gauge__bar-pending"
        :style="{ width: `${pendingPercentage}%`, left: `${spentPercentage}%` }"
      ></div>
    </div>

    <div class="budget-gauge__legend">
      <span class="budget-gauge__legend-item">
        <span class="budget-gauge__dot"></span>
        Spent: {{ formatAmount(spentMinor) }}
      </span>
      <span v-if="pendingAmountMinor > 0" class="budget-gauge__legend-item">
        <span class="budget-gauge__dot budget-gauge__dot--striped"></span>
        This award: {{ formatAmount(pendingAmountMinor) }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { toCurrency } from '@/utils';

const props = defineProps({
  validation: {
    type: Object,
    required: true,
  },
  pendingAmountMinor: {
    type: Number,
    default: 0,
  },
});

const totalMinor = computed(() => props.validation.program_budget_minor ?? 0);
const spentMinor = computed(() => props.validation.program_budget_spent_minor ?? 0);
const remainingMinor = computed(() => props.validation.program_budget_remaining_minor ?? 0);

const spentPercentage = computed(() =>
  totalMinor.value > 0 ? Math.min((spentMinor.value / totalMinor.value) * 100, 100) : 0,
);

const pendingPercentage = computed(() =>
  totalMinor.value > 0 ? Math.min((props.pendingAmountMinor / totalMinor.value) * 100, 100 - spentPercentage.value) : 0,
);

const willExceedBudget = computed(() => spentMinor.value + props.pendingAmountMinor > totalMinor.value);

function formatAmount(amountMinor) {
  return toCurrency(amountMinor);
}
</script>

<style scoped lang="scss">
.budget-gauge {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
  }

  &__spending {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-30);

    strong {
      color: var(--palette-color-default);
      font-weight: 600;
    }
  }

  &__amount--warning {
    color: var(--palette-color-danger) !important;
  }

  &__total {
    font-size: 0.75rem;
    color: var(--palette-color-default-lighten-30);
    white-space: nowrap;
  }

  &__bar {
    position: relative;
    width: 100%;
    height: 0.5rem;
    border-radius: 0.25rem;
    background-color: var(--palette-color-default-lighten-90);
    overflow: hidden;
  }

  &__bar-spent {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: var(--palette-color-brand-primary);
    transition: width 0.3s ease;
  }

  &__bar-pending {
    position: absolute;
    top: 0;
    height: 100%;
    background: repeating-linear-gradient(
      -45deg,
      var(--palette-color-brand-primary) 0,
      var(--palette-color-brand-primary) 3px,
      rgba(255, 255, 255, 0.85) 3px,
      rgba(255, 255, 255, 0.85) 6px
    );
    transition: width 0.3s ease, left 0.3s ease;
  }

  &__legend {
    display: flex;
    gap: 1rem;
    font-size: 0.75rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
  }

  &__dot {
    width: 0.625rem;
    height: 0.625rem;
    border-radius: 0.1875rem;
    background: var(--palette-color-brand-primary);

    &--striped {
      background: repeating-linear-gradient(
        -45deg,
        var(--palette-color-brand-primary) 0,
        var(--palette-color-brand-primary) 3px,
        rgba(255, 255, 255, 0.85) 3px,
        rgba(255, 255, 255, 0.85) 6px
      );
    }
  }
}
</style>
